export type DynamicOptionValue = { value: string; label: string };

export const stringifyDynamicOption = ({ value, label }: DynamicOptionValue): string =>
  JSON.stringify({ value, label });

export const parseDynamicOption = (value: unknown): DynamicOptionValue | undefined => {
  if (typeof value !== 'string' || !value.startsWith('{')) return undefined;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && 'value' in parsed && 'label' in parsed) {
      return parsed as DynamicOptionValue;
    }
  } catch {
    return undefined;
  }
  return undefined;
};

export const getStringValue = (value: unknown): string | undefined => {
  if (typeof value === 'string') return parseDynamicOption(value)?.value ?? value;
  if (value && typeof value === 'object' && 'value' in value) {
    const variableValue = value as { value: string; variables?: Record<string, unknown> };
    // Field-mapped: the .value carries a `{{ var }}` template — there's no
    // literal to pass to the endpoint, treat as missing.
    if (variableValue.variables && Object.keys(variableValue.variables).length > 0) {
      return undefined;
    }
    return parseDynamicOption(variableValue.value)?.value ?? variableValue.value;
  }
  return undefined;
};

export const isFieldMappedValue = (
  value: unknown
): value is { value?: unknown; variables?: Record<string, unknown> } => {
  if (!value || typeof value !== 'object' || !('variables' in value)) return false;
  const variables = (value as { variables?: Record<string, unknown> }).variables;
  return Boolean(variables && Object.keys(variables).length > 0);
};
